import { ReactElement, useEffect, useState } from 'react';

import styled from 'styled-components';

import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/swiper.min.css';
import './styles.scss';

import { ButtonIcon, ConditionalLink, Flex } from 'components';
import { createUrl } from 'app/utils';
import useExams from 'features/exams/useExams';
import { StudentPracticeExamAssessmentStatusEnum } from 'features/exams';
import { useLocation, useParams } from 'react-router-dom';
import _ from 'lodash';

SwiperCore.use([Navigation]);

const QuestionLink = styled(ConditionalLink)`
  display: flex;
  flex-grow: 1;
  height: 60px;
  align-items: center;
  justify-content: center;
  cursor: ${(props: any) => (props['data-fake'] ? 'not-allowed' : 'pointer')};

  font-size: ${({ theme }) => theme.fontTypes.type6};
  font-weight: bold;

  color: ${(props: any) => {
    if (props['data-active']) {
      return `white`;
    }

    return props.theme.colors.gray_600;
  }};

  ${(props: any) => {
    return `
      &::after {
        display: block;
        content: '';
        width: ${props['data-active'] ? '100%' : '6px'};
        height: 4px;
        background: ${props['data-color'] ? props.theme.colors[props['data-color']] : 'transparent'};
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 0;
      }
    `;
  }}

  &:first-child {
    padding: 0 15px;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      ${mediaQueries.desktop} {
      }
    `;
  }}
`;

const ExamsPagination = (): ReactElement => {
  const [swiper, setSwiper] = useState<SwiperCore>(undefined!);

  const location = useLocation();
  const { examUuid, assessmentUuid, questionUuid } = useParams();

  const { questions, examsDetails } = useExams();

  const isResult = location.pathname.search('/result') !== -1;

  const showBar = examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.StartedNotFinished;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [questionUuid]);

  useEffect(() => {
    if (swiper && questionUuid) {
      const questionIndex = _.findIndex(questions, (q) => q.studentPracticeExamAssessmentQuestionUuid === questionUuid);

      swiper.slideTo(questionIndex);
    }
  }, [swiper, questions, questionUuid]);

  return (
    <Flex flexGrow={0} bg="dark_350" flexWrap="nowrap">
      <ButtonIcon
        icon="chevronLeft"
        display={{ m: 'none', t: 'flex' }}
        width={{ t: 60, d: 80 }}
        iconWidth={22}
        className="exams-prev"
        bg="transparent"
      />

      <Swiper
        navigation={{
          prevEl: '.exams-prev',
          nextEl: '.exams-next',
        }}
        slidesPerView="auto"
        spaceBetween={1}
        loop={false}
        className="exams-pagination"
        onSwiper={(swiper) => {
          setSwiper(swiper);
        }}
      >
        <SwiperSlide>
          <QuestionLink
            component={Flex}
            data-active={!questionUuid && !isResult}
            type={
              examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.StartedNotFinished ||
              examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.AvailableToBuy
                ? 'internal'
                : 'nolink'
            }
            to={createUrl('exams/start', [examUuid])}
          >
            Abertura
          </QuestionLink>
        </SwiperSlide>

        {!!questions?.length &&
          questions?.map((question, index) => {
            const hasQuestionUuid = !!question.studentPracticeExamAssessmentQuestionUuid;
            const hasAnswerChoice = !!question.studentPracticeExamAssessmentQuestionAnswerChoiceUuid;
            const hasResult = question.rightAnswer !== null;

            const active = hasQuestionUuid && questionUuid === question.studentPracticeExamAssessmentQuestionUuid;
            const color = hasResult
              ? question.isQuestionInvalidated
                ? 'warning_400'
                : question.rightAnswer
                ? 'primary_400'
                : 'alert_400'
              : !hasAnswerChoice
              ? showBar
                ? 'white'
                : null
              : null;

            // console.log('hasQuestionUuid', hasQuestionUuid);
            // console.log('hasAnswerChoice', hasAnswerChoice);
            // console.log('hasResult', hasResult);
            // console.log('active', active);
            // console.log('color', color);

            return (
              <SwiperSlide key={`exam-pagination-${index.toString()}`}>
                <QuestionLink
                  component={Flex}
                  type={hasQuestionUuid ? 'internal' : 'nolink'}
                  to={createUrl('exams/question', [examUuid, assessmentUuid, question.studentPracticeExamAssessmentQuestionUuid])}
                  data-active={active}
                  data-color={color}
                >
                  {String(question?.order).padStart(2, '0')}
                </QuestionLink>
              </SwiperSlide>
            );
          })}

        <SwiperSlide>
          <QuestionLink
            component={Flex}
            data-active={location.pathname.search('/result') !== -1}
            type={examsDetails?.assessmentStatus === StudentPracticeExamAssessmentStatusEnum.Finished ? 'internal' : 'nolink'}
            to={createUrl('exams/result', [examUuid, assessmentUuid || examsDetails?.studentPracticeExamAssessment])}
          >
            Resultado
          </QuestionLink>
        </SwiperSlide>
      </Swiper>

      <ButtonIcon
        icon="chevronRight"
        display={{ m: 'none', t: 'flex' }}
        width={{ t: 60, d: 79 }}
        iconWidth={22}
        className="exams-next"
        bg="transparent"
      />
    </Flex>
  );
};

export default ExamsPagination;
